import * as preact from "preact";
import PropTypes from "prop-types";

import CheckBox from "../Checkbox";

import { getRandomString } from "../../foundation/utils/string";
import classname from "../../foundation/utils/classname";
import { imageUrl } from "../../foundation/utils/image";
import noderef from "../../foundation/utils/noderef";

import "./style.css";

const { h } = preact;

class InputWithLabel extends preact.Component {
    constructor(props) {
        super(props);
        this.state = {
            value: props.value,
            focusWithin: false,
            isMasked: props.type === "password"
        };
    }

    handleInitialInteractions = () => {
        const inputWithLabelContainerRef = this.nodeRefs && this.nodeRefs.inputWithLabelContainerRef;
        const input = inputWithLabelContainerRef && inputWithLabelContainerRef.querySelector("input");

        if (!input) {
            return;
        }

        const { autofocus, autohighlight } = this.props;

        autofocus && input.focus();
        autohighlight && input.select();
    }

    handleInputChange = ev => {
        const { value } = ev.target;
        this.setState({
            value
        });

        const { onInput } = this.props;
        onInput(value);
    }

    handleBlur = () => {
        const { onBlur, label } = this.props;
        if (onBlur) {
            onBlur(label);
        }
        this.setState({
            focusWithin: false
        });
    }

    handleFocus = () => {
        const { onFocus, label } = this.props;
        if (onFocus) {
            onFocus(label);
        }

        this.setState({
            focusWithin: true
        });
    }

    handleMaskChange = () => {
        this.setState(prevState => ({ isMasked: !prevState.isMasked }));
    }

    componentDidMount() {
        this.handleInitialInteractions();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.value !== this.props.value) {
            this.setState ({
                value: nextProps.value
            });
        }
    }

    render() {
        const { value, focusWithin, isMasked } = this.state;
        const {
            class: cssClass,
            label,
            onKeyPress,
            primary,
            valid,
            extraInfoText,
            extraInfoLoading,
            tipText,
            showTipText,
            minLength,
            maxLength,
            autoComplete,
            inputRef,
            type
        } = this.props;

        let extraInfoImgUrl;
        if (primary && valid) {
            extraInfoImgUrl = "img/icTickBlue.svg";
        } else if (valid) {
            extraInfoImgUrl = "img/icTick.svg";
        } else if (valid === false) {
            extraInfoImgUrl = "img/icInvalid.svg";
        }
        // Password changes to text when 'show' checked
        let activeType = type;
        const isPassword = type === "password";

        if (isPassword && !isMasked) {
            activeType = "text";
        }

        const id = getRandomString();

        const renderTipText = (focusWithin || showTipText) && !!tipText;
        const inputAreaClasses = classname("inputArea", { focus: focusWithin, typed: !!value });

        return (
            <div
                ref={noderef(this, "inputWithLabelContainerRef")}
                class={classname("inputWithLabelContainer", cssClass, { primary, invalid: valid === false })}>
                <div class={inputAreaClasses} title={label}>
                    <input
                        id={id}
                        value={value}
                        name="textInput"
                        type={activeType}
                        placeholder={label}
                        minLength={minLength}
                        maxLength={maxLength}
                        ref={inputRef}
                        autoComplete={autoComplete}
                        onInput={this.handleInputChange}
                        onBlur={this.handleBlur}
                        onFocus={this.handleFocus}
                        onKeyPress={onKeyPress}
                        data-testid="inputWithLabelInput"/>
                    <label for={id} class="inputLabel" data-testid="inputWithLabelTitle">{label}</label>
                    <div class="extraInputInfo">
                        {extraInfoLoading && <img src={imageUrl("img/icSpinnerBlueTheme.svg")} class="spinner" data-testid="inputWithLabelLoading"/>}
                        {!!extraInfoImgUrl && <img src={imageUrl(extraInfoImgUrl)} data-testid="inputWithLabelExtraInfoImg"/>}
                        <span class="extraInfoText" data-testid="inputWithLabelExtraInfoText">{extraInfoText}</span>
                        {isPassword && (
                            <label class="showPasswordContainer">
                                <CheckBox onChange={this.handleMaskChange} checked={!isMasked} data-testid="inputWithLabelShowPassword"/>
                                <span class="showText">Show</span>
                            </label>
                        )}
                    </div>
                </div>
                {renderTipText && (
                    <div class="tipText" data-testid="inputWithLabelTipText">{tipText}</div>
                )}
            </div>
        );
    }
}

InputWithLabel.defaultProps = {
    value: "",
    type: "text",
    maxLength: 100,
    autoComplete: "off",
    autofocus: false,
    autohighlight: false
};

InputWithLabel.propTypes = {
    /** Value to be shown at input container. */
    value: PropTypes.string,
    /**  */
    label: PropTypes.string,
    /** Callback to be invoked when user pressed a key. */
    onKeyPress: PropTypes.func,
    /** Callback to called when the input is edited. */
    onInput: PropTypes.func,
    /** Callback to called when the input on blur. */
    onBlur: PropTypes.func,
    /** Callback to called when the input on focus. */
    onFocus: PropTypes.func,
    /** Whether to apply Zeplin primary button theme or not. */
    primary: PropTypes.bool,
    /** Whether the input is valid. */
    valid: PropTypes.bool,
    /** Extra information inside input. */
    extraInfoText: PropTypes.string,
    /** Whether the input is being processed. */
    extraInfoLoading: PropTypes.bool,
    /** Tip about input */
    tipText: PropTypes.object,
    /** Whether tip text visible without focus. */
    showTipText: PropTypes.boolean,
    /** HTML maxLength attribute. */
    maxLength: PropTypes.number,
    /** HTML autoComplete attribute. */
    autoComplete: PropTypes.string,
    /** HTML input type attribute. */
    type: PropTypes.string,
    /** Forwarding to input ref. */
    inputRef: PropTypes.func
};

export default InputWithLabel;
